import { useState, useEffect, useContext } from 'react'
import { ref, onChildChanged, onValue, DataSnapshot } from 'firebase/database'
import {
  RealtimeDBIdentityVerificationStatus,
  VerificationStatus,
} from '@playback-rewards/shared-libraries'

//contexts
import AuthContext from 'context/AuthContext'
import LoaderContext from 'context/LoaderContext'
//configs
import { db } from 'config/firebaseConfig'
//services
import { getUserById } from 'services/userService'
//utils
import { handleError, isUserOutOfAttempts } from 'utils/utils'

const USER_REFRESH_POLL_INTERVAL_MS = 2000
const USER_REFRESH_MAX_ATTEMPTS = 10

const useRealtimeIdentityVerificationStatus = (userId?: string) => {
  const { updateUserData } = useContext(AuthContext)
  const { showLoader, hideLoader } = useContext(LoaderContext)

  const [verificationResult, setVerificationResult] = useState()
  const [outOfAttempts, setOutOfAttempts] = useState(false)

  useEffect(() => {
    if (!userId) {
      return
    }
    const notificationRef = ref(db, `identity_verification/${userId}`)
    let isActive = true
    let pollTimeoutId: ReturnType<typeof setTimeout> | undefined
    let pollToken = 0

    const clearPollTimeout = () => {
      if (pollTimeoutId) {
        clearTimeout(pollTimeoutId)
        pollTimeoutId = undefined
      }
    }

    const refreshUser = async (attempt = 1, token = pollToken): Promise<void> => {
      try {
        const data = await getUserById(userId)

        if (!isActive || token !== pollToken) {
          return
        }

        updateUserData(data.data)
        setOutOfAttempts(isUserOutOfAttempts(data.data))

        if (
          data.data?.identityVerification?.status === VerificationStatus.VERIFYING &&
          attempt < USER_REFRESH_MAX_ATTEMPTS
        ) {
          clearPollTimeout()
          pollTimeoutId = setTimeout(() => {
            void refreshUser(attempt + 1, token)
          }, USER_REFRESH_POLL_INTERVAL_MS)
        }
      } catch (error) {
        if (isActive && token === pollToken) {
          handleError(error)
        }
      }
    }

    const notificationHandler = async (snapshot: DataSnapshot) => {
      if (!isActive) {
        return
      }

      const snapshotValue = snapshot.val()
      const state = typeof snapshotValue === 'string' ? snapshotValue : snapshotValue?.state

      setVerificationResult(state)
      showLoader()
      if (state === RealtimeDBIdentityVerificationStatus.FINISHED) {
        clearPollTimeout()
        pollToken += 1
        await refreshUser(1, pollToken)
      }

      if (isActive) {
        hideLoader()
      }
    }
    const unsubscribeOnValue = onValue(notificationRef, notificationHandler)
    const unsubscribeOnChildChanged = onChildChanged(notificationRef, notificationHandler)

    return () => {
      isActive = false
      pollToken += 1
      clearPollTimeout()
      unsubscribeOnValue()
      unsubscribeOnChildChanged()
    }
  }, [userId])

  return { verificationResult, outOfAttempts }
}

export default useRealtimeIdentityVerificationStatus
